/**
 * The one-line verdict at the top of the Package check section (design: docs/plans/2026-11-03-package-check-design.md).
 *
 * PURE: no React, no Firebase, so it is exercised from functions' vitest (npm run test:web).
 *
 * "Ready to hand off" is said only when the package check actually looked at something and found nothing open. A check that
 * could not run (its documents are absent) is NOT a clean result, so a package where nothing could be judged says so instead
 * of saying "Ready", and a package where some checks could not run says which, beside the verdict.
 */
export interface PackageCheckLike {
  checkId: string;
  status: string;
  /** The plain title of the check, for the "not checked" line. */
  title: string;
}

export interface PackageVerdict {
  tone: "clear" | "attention" | "neutral";
  title: string;
  subtitle: string;
}

export function packageVerdict(input: { checks: PackageCheckLike[]; activeCount: number; declinedCount: number }): PackageVerdict {
  const { checks, activeCount, declinedCount } = input;
  if (checks.length === 0) {
    return { tone: "neutral", title: "The package check has not run yet", subtitle: "It has not produced a result yet. It appears here after the next verification run." };
  }
  const skipped = checks.filter((c) => c.status === "SKIPPED");
  const notChecked = skipped.length ? ` Not checked yet: ${skipped.map((c) => c.title.charAt(0).toLowerCase() + c.title.slice(1)).join("; ")}.` : "";
  if (activeCount > 0) {
    return {
      tone: "attention",
      title: `${activeCount} thing${activeCount === 1 ? "" : "s"} to fix before hand-off`,
      subtitle: `Each one names the document and what to do. These only warn: nothing is blocked, and you can decline an item with a reason.${notChecked}`,
    };
  }
  if (skipped.length === checks.length) {
    return { tone: "neutral", title: "Not enough has been generated to check the package", subtitle: `Generate the study documents, then press Re-run.${notChecked}` };
  }
  return {
    tone: "clear",
    title: "Ready to hand off",
    subtitle: `The documents agree with each other and with your approved forms.${declinedCount > 0 ? ` ${declinedCount} item${declinedCount === 1 ? " was" : "s were"} declined.` : ""}${notChecked}`,
  };
}
